import { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react'

import { Anchor, Point, anchored, Rect } from '@st/geom'
import { ViewportGeometry, getTargetScrollPosition } from '@st/geom/viewport'

type ScrollCalculation = {
  targetViewportRect: Rect
  targetScrollPosition: Point
}

const ZeroRect: Rect = { x: 0, y: 0, width: 0, height: 0 }

export function ScrollGeometryWorkbench() {
  const scrollViewRef = useRef<HTMLDivElement>(null)
  const elementRef = useRef<HTMLDivElement>(null)

  const [geom, setGeom] = useState<ViewportGeometry>({
    childRect: ZeroRect,
    viewportRect: ZeroRect,
    scrollAreaRect: ZeroRect
  })
  const [viewportAnchor, setViewportAnchor] = useState(Anchor.center)
  const [childAnchor, setChildAnchor] = useState(Anchor.center)

  const calculation = useMemo(() => {
    return {
      targetScrollPosition: getTargetScrollPosition({ geom, viewportAnchor, childAnchor }),
      targetViewportRect: anchored({
        follower: geom.viewportRect,
        target: geom.childRect,
        followerAnchor: viewportAnchor,
        targetAnchor: childAnchor
      })
    } as ScrollCalculation
  }, [geom, viewportAnchor, childAnchor])

  function updateGeom() {
    const scrollView = scrollViewRef.current
    const element = elementRef.current
    if (!scrollView || !element) return

    setGeom(getViewportGeometry(scrollView, element))
  }

  useEffect(() => {
    updateGeom()
  }, [viewportAnchor, childAnchor])

  useEffect(() => {
    setTimeout(() => updateGeom(), 0)

    const scroller = scrollViewRef.current

    if (!scroller) {
      return
    }

    scroller.addEventListener('scroll', updateGeom)
    return () => scroller.removeEventListener('scroll', updateGeom)
  }, [])

  return (
    <div className="grid h-screen w-screen grid-cols-2 gap-0 overflow-hidden bg-gray-100">
      {/* Column 1: Actual Scroll View */}
      <div className="flex items-center justify-center overflow-hidden bg-gray-100 p-5">
        <div
          ref={scrollViewRef}
          className="relative h-[200px] w-[300px] rounded-lg border-2 border-blue-500 bg-white shadow-lg"
          style={{ overflow: 'overlay' }}
        >
          <p className="m-0 h-[600px] w-[800px]">
            Far out in the uncharted backwaters of the unfashionable end of the western spiral arm
            of the Galaxy lies a small unregarded yellow sun. Orbiting this at a distance of roughly
            ninety-two million miles is an utterly insignificant little blue green planet whose
            ape-descended life forms are so amazingly primitive that they still think digital
            watches are a pretty neat idea.
          </p>

          <div
            ref={elementRef}
            className="absolute left-[300px] top-[250px] h-[30px] w-[30px] rounded bg-red-500"
          />
        </div>
      </div>

      {/* Column 2: Map Visualization */}
      <div className="grid grid-rows-[auto_1fr] gap-5 overflow-hidden bg-gray-50 p-5">
        <ScrollControls
          viewportAnchor={viewportAnchor}
          childAnchor={childAnchor}
          onViewportAnchorChange={setViewportAnchor}
          onChildAnchorChange={setChildAnchor}
          calculation={calculation}
          geom={geom}
          scrollViewRef={scrollViewRef}
        />

        <ScrollMap
          rects={[
            labeled(geom.scrollAreaRect, '#e5e7eb'),
            labeled(geom.viewportRect, '#3b82f6'),
            labeled(calculation.targetViewportRect, '#10b981'),
            labeled(geom.childRect, '#ef4444')
          ]}
        />
      </div>
    </div>
  )
}

type LabeledRect = Rect & {
  color: string
}

type RectVisualizerProps = {
  rects: LabeledRect[]
  legend?: ReactNode
}
function RectVisualizer({ rects, legend }: RectVisualizerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 400, height: 300 }) // Default size

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect()
        if (width > 0 && height > 0) {
          setContainerSize({ width, height })
        }
      }
    }

    updateSize()
    const resizeObserver = new ResizeObserver(updateSize)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [])

  // Find the scroll area rect (the largest one)
  const scrollAreaRect = rects.find((r) => r.color === '#e5e7eb')
  if (!scrollAreaRect) return null

  // Calculate scale to fit within container with proper padding
  const padding = 40
  const availableWidth = containerSize.width - padding
  const availableHeight = containerSize.height - padding

  const scaleX = availableWidth / scrollAreaRect.width
  const scaleY = availableHeight / scrollAreaRect.height
  const scale = Math.min(scaleX, scaleY, 1) // Don't scale up

  return (
    <div ref={containerRef} className="flex h-full w-full items-center justify-center">
      <div
        style={{
          position: 'relative',
          transform: `scale(${scale})`,
          transformOrigin: 'center',
          width: scrollAreaRect.width,
          height: scrollAreaRect.height
        }}
      >
        {rects.map((rect, index) => {
          const isOutline = rect.color === '#3b82f6' || rect.color === '#10b981'
          // Adjust positions relative to scroll area
          const relativeRect = {
            ...rect,
            x: rect.x - scrollAreaRect.x,
            y: rect.y - scrollAreaRect.y
          }

          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: relativeRect.y,
                left: relativeRect.x,
                width: rect.width,
                height: rect.height,
                backgroundColor: isOutline ? 'transparent' : rect.color,
                border: isOutline ? `3px solid ${rect.color}` : 'none',
                opacity: rect.color === '#e5e7eb' ? 0.3 : 1,
                borderRadius: rect.color === '#ef4444' ? '4px' : '0'
              }}
            />
          )
        })}
      </div>
      {legend}
    </div>
  )
}

function labeled(rect: Rect, color: string): LabeledRect {
  return { ...rect, color }
}

function getViewportGeometry(
  scrollElement: HTMLElement,
  childElement: HTMLElement
): ViewportGeometry {
  const viewportDOMRect = scrollElement.getBoundingClientRect()
  const childDOMRect = childElement.getBoundingClientRect()

  // Calculate scroll area rect (the entire scrollable content area)
  const scrollAreaRect = {
    x: viewportDOMRect.x - scrollElement.scrollLeft,
    y: viewportDOMRect.y - scrollElement.scrollTop,
    width: scrollElement.scrollWidth,
    height: scrollElement.scrollHeight
  }

  return {
    viewportRect: {
      x: viewportDOMRect.x,
      y: viewportDOMRect.y,
      // Use clientWidth/Height to get the actual visible area (excludes scrollbars)
      width: scrollElement.clientWidth,
      height: scrollElement.clientHeight
    },
    childRect: {
      x: childDOMRect.x,
      y: childDOMRect.y,
      width: childDOMRect.width,
      height: childDOMRect.height
    },
    scrollAreaRect: scrollAreaRect
  }
}

type AnchorSelectProps = {
  value: Anchor
  onChange: (anchor: Anchor) => void
}
function AnchorSelect({ value, onChange }: AnchorSelectProps) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as Anchor)}
      className="min-w-[120px] cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {Object.values(Anchor).map((anchor) => (
        <option key={anchor} value={anchor}>
          {anchor}
        </option>
      ))}
    </select>
  )
}

type ScrollControlsProps = {
  viewportAnchor: Anchor
  childAnchor: Anchor
  onViewportAnchorChange: (anchor: Anchor) => void
  onChildAnchorChange: (anchor: Anchor) => void
  calculation: ScrollCalculation
  geom: ViewportGeometry
  scrollViewRef: RefObject<HTMLDivElement | null>
}

function ScrollControls({
  viewportAnchor,
  childAnchor,
  onViewportAnchorChange,
  onChildAnchorChange,
  calculation,
  geom,
  scrollViewRef
}: ScrollControlsProps) {
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <h3 className="m-0 mb-4 text-base font-semibold">Scroll Controls</h3>
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column: Controls */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">Viewport Anchor</label>
            <AnchorSelect value={viewportAnchor} onChange={onViewportAnchorChange} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">Element Anchor</label>
            <AnchorSelect value={childAnchor} onChange={onChildAnchorChange} />
          </div>
          <button
            onClick={() => {
              scrollViewRef.current!.scrollTo({
                left: calculation.targetScrollPosition.x,
                top: calculation.targetScrollPosition.y,
                behavior: 'smooth'
              })
            }}
            className="rounded-md bg-blue-500 px-4 py-2 font-medium text-white hover:bg-blue-600"
          >
            Scroll to Target
          </button>
        </div>

        {/* Right Column: Information */}
        <div className="space-y-3 text-sm">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Scroll Area Dimensions
            </div>
            <div className="font-mono text-gray-900">
              ({geom.scrollAreaRect.width}, {geom.scrollAreaRect.height})
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Current Viewport (relative)
            </div>
            <div className="font-mono text-gray-900">
              ({geom.viewportRect.x - geom.scrollAreaRect.x},{' '}
              {geom.viewportRect.y - geom.scrollAreaRect.y}, {geom.viewportRect.width},{' '}
              {geom.viewportRect.height})
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Target Viewport (relative)
            </div>
            <div className="font-mono text-gray-900">
              ({calculation.targetViewportRect.x - geom.scrollAreaRect.x},{' '}
              {calculation.targetViewportRect.y - geom.scrollAreaRect.y},{' '}
              {calculation.targetViewportRect.width}, {calculation.targetViewportRect.height})
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Element (relative)
            </div>
            <div className="font-mono text-gray-900">
              ({geom.childRect.x - geom.scrollAreaRect.x},{' '}
              {geom.childRect.y - geom.scrollAreaRect.y}, {geom.childRect.width},{' '}
              {geom.childRect.height})
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Target Scroll Position
            </div>
            <div className="font-mono text-gray-900">
              ({calculation.targetScrollPosition.x}, {calculation.targetScrollPosition.y})
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ScrollMapLegend() {
  return (
    <div className="absolute right-2 top-2 max-w-[140px] rounded-md border bg-white p-3 shadow-sm">
      <div className="flex flex-col gap-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 border border-gray-300 bg-gray-200" />
          <span>Scroll Area</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 border-2 border-blue-500 bg-transparent" />
          <span>Current Viewport</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 border-2 border-green-500 bg-transparent" />
          <span>Target Viewport</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 bg-red-500" />
          <span>Element</span>
        </div>
      </div>
    </div>
  )
}

type ScrollMapProps = {
  rects: LabeledRect[]
}

function ScrollMap({ rects }: ScrollMapProps) {
  return (
    <div className="grid min-h-0 grid-rows-[auto_1fr] gap-4 rounded-lg bg-white p-5 shadow-sm">
      <h3 className="m-0 text-base font-semibold">Scroll Map</h3>
      <div className="relative h-full max-h-full w-full max-w-full overflow-hidden">
        <RectVisualizer rects={rects} legend={<ScrollMapLegend />} />
      </div>
    </div>
  )
}
